import { useState } from "react";

// ===== PAIRWISE QUESTION DATA =====

const WHITE_QUESTIONS = [
  {
    id: "w1",
    style: "white",
    a: {
      name: "Kim Crawford Sauvignon Blanc",
      descriptor: "Crisp, zesty, unoaked",
      region: "Marlborough, New Zealand",
      effects: { acidity: +1, oak: -1, body: -1 }
    },
    b: {
      name: "Sonoma-Cutrer Chardonnay",
      descriptor: "Rich, buttery, lightly oaked",
      region: "Russian River Valley, California",
      effects: { oak: +1, body: +1, acidity: -1 }
    }
  },
  {
    id: "w2",
    style: "white",
    a: {
      name: "Chablis",
      descriptor: "Pure, mineral, bone dry",
      region: "Burgundy, France",
      effects: { acidity: +1, body: -1, earthiness: +1 }
    },
    b: {
      name: "White Burgundy Village",
      descriptor: "Richer texture, light oak",
      region: "Burgundy, France",
      effects: { body: +1, oak: +1, acidity: -1 }
    }
  },
  {
    id: "w3",
    style: "white",
    a: {
      name: "Dry Riesling",
      descriptor: "Aromatic, floral, high acid",
      region: "Alsace, France",
      effects: { fruit_forward: +1, acidity: +1 }
    },
    b: {
      name: "Sancerre",
      descriptor: "Grassy, citrus, mineral",
      region: "Loire Valley, France",
      effects: { acidity: +1, earthiness: +1, fruit_forward: -1 }
    }
  },
  {
    id: "w4",
    style: "white",
    a: {
      name: "Pouilly-Fumé",
      descriptor: "Herbaceous, mineral, precise",
      region: "Loire Valley, France",
      effects: { acidity: +1, earthiness: +1, oak: -1, body: -1 }
    },
    b: {
      name: "Meursault",
      descriptor: "Rich, textured, lightly oaked",
      region: "Burgundy, France",
      effects: { body: +1, oak: +1, acidity: -1 }
    }
  },
  {
    id: "w5",
    style: "white",
    a: {
      name: "Grüner Veltliner",
      descriptor: "Peppery, mineral, dry",
      region: "Wachau, Austria",
      effects: { acidity: +1, earthiness: +1, fruit_forward: -1, oak: -1 }
    },
    b: {
      name: "White Rioja",
      descriptor: "Oaked, textured, full",
      region: "Rioja, Spain",
      effects: { oak: +1, body: +1, fruit_forward: +1, acidity: -1 }
    }
  }
];

const RED_QUESTIONS = [
  {
    id: "r1",
    style: "red",
    a: {
      name: "Caymus Cabernet Sauvignon",
      descriptor: "Full-bodied, rich, bold tannins",
      region: "Napa Valley, California",
      effects: { body: +1, tannin: +1, fruit_forward: +1 }
    },
    b: {
      name: "Merry Edwards Pinot Noir",
      descriptor: "Light, silky, elegant",
      region: "Sonoma County, California",
      effects: { body: -1, tannin: -1, earthiness: +1 }
    }
  },
  {
    id: "r2",
    style: "red",
    a: {
      name: "Napa Cabernet",
      descriptor: "Fruit-forward, plush, powerful",
      region: "Napa Valley, California",
      effects: { fruit_forward: +1, body: +1 }
    },
    b: {
      name: "Bordeaux",
      descriptor: "Earthy, structured, precise",
      region: "Bordeaux, France",
      effects: { earthiness: +1, acidity: +1, tannin: +1 }
    }
  },
  {
    id: "r3",
    style: "red",
    a: {
      name: "Malbec",
      descriptor: "Plush, dark fruit, smooth",
      region: "Mendoza, Argentina",
      effects: { fruit_forward: +1, body: +1 }
    },
    b: {
      name: "Chianti",
      descriptor: "Earthy, acidic, food-driven",
      region: "Tuscany, Italy",
      effects: { acidity: +1, earthiness: +1, tannin: +1 }
    }
  },
  {
    id: "r4",
    style: "red",
    a: {
      name: "Barolo",
      descriptor: "Austere, structured, high acid",
      region: "Piedmont, Italy",
      effects: { tannin: +1, acidity: +1, earthiness: +1, fruit_forward: -1 }
    },
    b: {
      name: "Côtes du Rhône",
      descriptor: "Plush, fruit-forward, easy",
      region: "Rhône Valley, France",
      effects: { fruit_forward: +1, body: +1, tannin: -1, acidity: -1 }
    }
  },
  {
    id: "r5",
    style: "red",
    a: {
      name: "Rioja Reserva",
      descriptor: "Earthy, precise, medium-weight",
      region: "Rioja, Spain",
      effects: { earthiness: +1, acidity: +1, fruit_forward: -1 }
    },
    b: {
      name: "Amarone della Valpolicella",
      descriptor: "Concentrated, powerful, rich fruit",
      region: "Veneto, Italy",
      effects: { body: +1, fruit_forward: +1, tannin: +1, acidity: -1 }
    }
  }
];

// ===== SCORING HELPERS =====

function clamp(v) {
  return Math.min(5, Math.max(1, v));
}

function scoreDimensions(baseDims, questions, answers) {
  const dims = { ...baseDims };
  const relevantAnswers = answers.filter(a =>
    questions.some(q => q.id === a.id)
  );

  relevantAnswers.forEach(answer => {
    if (answer.choice === "skip") return;
    const q = questions.find(q => q.id === answer.id);
    if (!q) return;
    const effects = answer.choice === "a" ? q.a.effects : q.b.effects;
    Object.entries(effects).forEach(([dim, delta]) => {
      if (dim in dims) dims[dim] = clamp(dims[dim] + delta);
    });
  });

  const answeredCount = relevantAnswers.filter(a => a.choice !== "skip").length;
  const confidence = answeredCount >= 2 ? "high" : answeredCount === 1 ? "medium" : "low";

  return Object.fromEntries(
    Object.entries(dims).map(([k, v]) => [k, { score: v, confidence }])
  );
}

function buildPairwiseQueue(styles) {
  const queue = [];
  const needsWhite = styles.some(
    s => s.toLowerCase().includes("white") || s.includes("move around")
  );
  const needsRed = styles.some(
    s => s.toLowerCase().includes("red") || s.includes("move around")
  );
  if (needsWhite) WHITE_QUESTIONS.forEach(q => queue.push(q));
  if (needsRed) RED_QUESTIONS.forEach(q => queue.push(q));
  return queue;
}

function buildProfile({ experienceLevel, selectedStyles, pairwiseAnswers, loves, avoids, dietary }) {
  const needsWhite = selectedStyles.some(
    s => s.toLowerCase().includes("white") || s.includes("move around")
  );
  const needsRed = selectedStyles.some(
    s => s.toLowerCase().includes("red") || s.includes("move around")
  );
  const needsRosé = selectedStyles.some(s => s.includes("rosé"));
  const needsSparkling = selectedStyles.some(s => s.includes("Sparkling"));

  const taste_dimensions = {};

  if (needsWhite) {
    taste_dimensions.white = scoreDimensions(
      { sweetness: 1, body: 3, acidity: 3, oak: 3, fruit_forward: 3, earthiness: 3 },
      WHITE_QUESTIONS,
      pairwiseAnswers
    );
  }

  if (needsRed) {
    taste_dimensions.red = scoreDimensions(
      { sweetness: 1, body: 3, tannin: 3, acidity: 3, fruit_forward: 3, earthiness: 3 },
      RED_QUESTIONS,
      pairwiseAnswers
    );
  }

  if (needsRosé) taste_dimensions.rosé = { context_only: true };
  if (needsSparkling) taste_dimensions.sparkling = { preference: "ordered" };

  return {
    profile_id:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Date.now().toString(),
    experience_level: experienceLevel,
    taste_dimensions,
    explicit_loves: loves
      .filter(l => l.trim())
      .map(l => ({ type: "wine", value: l.trim() })),
    explicit_avoids: avoids
      .filter(a => a.trim())
      .map(a => ({ type: "style", value: a.trim() })),
    dietary,
    price_sensitivity: null,
    profile_notes: [],
    profile_status: "complete"
  };
}

// ===== STYLE / EXPERIENCE OPTIONS =====

const STYLE_OPTIONS = [
  "Crisp, light whites",
  "Rich, full whites",
  "Dry rosé",
  "Light to medium reds",
  "Bold, full reds",
  "Sparkling / Champagne",
  "I move around — depends on the night"
];

const EXPERIENCE_OPTIONS = [
  { value: "novice", label: "Still exploring", desc: "I'm figuring out what I like" },
  { value: "developing", label: "Getting the hang of it", desc: "I know some styles I enjoy" },
  { value: "enthusiast", label: "Pretty into it", desc: "I have strong preferences and follow wine closely" },
  { value: "serious", label: "Seriously into it", desc: "I buy, collect, or study wine" }
];

// ===== STYLES =====

const s = {
  wrapper: {
    backgroundColor: "#111",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center"
  },
  container: {
    backgroundColor: "#111",
    minHeight: "100vh",
    color: "#f0f0f0",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    width: "100%",
    maxWidth: "480px",
    display: "flex",
    flexDirection: "column"
  },
  screen: {
    flex: 1,
    padding: "40px 24px 40px",
    display: "flex",
    flexDirection: "column"
  },
  headline: {
    fontSize: "26px",
    fontWeight: "600",
    color: "#fff",
    lineHeight: "1.3",
    marginBottom: "10px"
  },
  subtext: {
    fontSize: "15px",
    color: "#777",
    lineHeight: "1.6",
    marginBottom: "32px"
  },
  card: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #2a2a2a",
    borderRadius: "16px",
    padding: "18px 20px",
    cursor: "pointer",
    marginBottom: "10px",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent"
  },
  cardSelected: {
    borderColor: "#8B4513",
    backgroundColor: "#1e1510"
  },
  cardName: {
    fontSize: "17px",
    fontWeight: "600",
    color: "#fff",
    marginBottom: "4px"
  },
  cardDescriptor: {
    fontSize: "14px",
    color: "#888",
    marginBottom: "2px"
  },
  cardRegion: {
    fontSize: "13px",
    color: "#555"
  },
  primaryButton: {
    width: "100%",
    backgroundColor: "#8B4513",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontSize: "17px",
    fontWeight: "600",
    padding: "16px",
    cursor: "pointer",
    marginTop: "auto",
    flexShrink: 0
  },
  primaryButtonDisabled: {
    backgroundColor: "#2a1e17",
    color: "#555",
    cursor: "not-allowed"
  },
  skipLink: {
    textAlign: "center",
    color: "#555",
    fontSize: "15px",
    cursor: "pointer",
    padding: "20px 0 4px",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent"
  },
  orDivider: {
    textAlign: "center",
    color: "#333",
    fontSize: "13px",
    padding: "8px 0"
  },
  sectionLabel: {
    fontSize: "12px",
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginBottom: "10px",
    marginTop: "4px"
  },
  textInput: {
    width: "100%",
    backgroundColor: "#1a1a1a",
    border: "1px solid #2a2a2a",
    borderRadius: "12px",
    color: "#f0f0f0",
    fontSize: "15px",
    padding: "14px",
    fontFamily: "inherit",
    boxSizing: "border-box",
    marginBottom: "10px",
    outline: "none"
  },
  addMore: {
    color: "#8B4513",
    fontSize: "14px",
    cursor: "pointer",
    userSelect: "none",
    marginBottom: "20px",
    display: "inline-block"
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "18px 0",
    borderBottom: "1px solid #1e1e1e",
    cursor: "pointer",
    userSelect: "none"
  },
  checkboxBox: {
    width: "22px",
    height: "22px",
    borderRadius: "6px",
    border: "2px solid #333",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  checkboxBoxChecked: {
    backgroundColor: "#8B4513",
    borderColor: "#8B4513"
  },
  styleTagRow: {
    fontSize: "12px",
    color: "#555",
    marginBottom: "32px",
    lineHeight: "1.6"
  }
};

// ===== SUB-COMPONENTS =====

function ProgressBar({ progress }) {
  return (
    <div style={{ height: "2px", backgroundColor: "#1a1a1a", flexShrink: 0 }}>
      <div
        style={{
          height: "100%",
          backgroundColor: "#8B4513",
          width: `${Math.round(progress * 100)}%`,
          transition: "width 0.35s ease"
        }}
      />
    </div>
  );
}

function WineCard({ wine, onSelect }) {
  return (
    <div style={s.card} onClick={onSelect}>
      <div style={s.cardName}>{wine.name}</div>
      <div style={s.cardDescriptor}>{wine.descriptor}</div>
      <div style={s.cardRegion}>{wine.region}</div>
    </div>
  );
}

// ===== MAIN COMPONENT =====

export default function Onboarding({ onComplete }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [experienceLevel, setExperienceLevel] = useState(null);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [pairwiseQueue, setPairwiseQueue] = useState([]);
  const [pairwiseAnswers, setPairwiseAnswers] = useState([]);
  const [loves, setLoves] = useState(["", ""]);
  const [avoids, setAvoids] = useState(["", ""]);
  const [completedProfile, setCompletedProfile] = useState(null);

  // Step layout:
  //   0 = welcome
  //   1 = experience level
  //   2 = style selection
  //   3..3+N-1 = pairwise questions (N = pairwiseQueue.length)
  //   3+N = loves/avoids
  //   3+N+1 = dietary flags
  //   3+N+2 = completion

  const STEPS_BEFORE_PAIRWISE = 3;
  const STEP_LOVES = STEPS_BEFORE_PAIRWISE + pairwiseQueue.length;
  const STEP_COMPLETE = STEP_LOVES + 1;
  const TOTAL_STEPS = STEP_COMPLETE + 1;

  // Progress advances through every screen including pairwise
  const progress = TOTAL_STEPS > 1 ? stepIdx / (TOTAL_STEPS - 1) : 0;

  function nextStep() {
    setStepIdx(i => i + 1);
  }

  function handleStylesContinue() {
    const queue = buildPairwiseQueue(selectedStyles);
    setPairwiseQueue(queue);
    nextStep();
  }

  function handlePairwiseAnswer(questionId, choice) {
    setPairwiseAnswers(prev => [
      ...prev.filter(a => a.id !== questionId),
      { id: questionId, choice }
    ]);
    nextStep();
  }

  function handleFinish() {
    const profile = buildProfile({
      experienceLevel,
      selectedStyles,
      pairwiseAnswers,
      loves,
      avoids,
      dietary: { vegan: false, sulfite_sensitive: false }
    });
    setCompletedProfile(profile);
    nextStep(); // → STEP_COMPLETE
  }

  // ===== SCREEN: WELCOME =====
  if (stepIdx === 0) {
    return (
      <div style={s.wrapper}>
        <div style={s.container}>
          <ProgressBar progress={0} />
          <div style={s.screen}>
            <div style={{ marginBottom: "auto" }}>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "600",
                  color: "#fff",
                  letterSpacing: "-0.5px",
                  marginBottom: "40px"
                }}
              >
                da<span style={{ color: "#8B4513" }}>vino</span>
              </div>
              <div style={s.headline}>
                Let's figure out what you actually like.
              </div>
              <div style={s.subtext}>
                Not what you're supposed to like — what you actually reach for.
                Takes about three minutes.
              </div>
            </div>
            <button style={s.primaryButton} onClick={nextStep}>
              Let's go
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== SCREEN: EXPERIENCE LEVEL =====
  if (stepIdx === 1) {
    return (
      <div style={s.wrapper}>
        <div style={s.container}>
          <ProgressBar progress={progress} />
          <div style={s.screen}>
            <div style={s.headline}>
              How would you describe your relationship with wine?
            </div>
            <div style={s.subtext}>
              No right answer — this just helps calibrate the recommendations.
            </div>
            {EXPERIENCE_OPTIONS.map(opt => (
              <div
                key={opt.value}
                style={{
                  ...s.card,
                  ...(experienceLevel === opt.value ? s.cardSelected : {})
                }}
                onClick={() => setExperienceLevel(opt.value)}
              >
                <div style={s.cardName}>{opt.label}</div>
                <div style={s.cardDescriptor}>{opt.desc}</div>
              </div>
            ))}
            <button
              style={{
                ...s.primaryButton,
                ...(experienceLevel ? {} : s.primaryButtonDisabled)
              }}
              onClick={nextStep}
              disabled={!experienceLevel}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== SCREEN: STYLE SELECTION =====
  if (stepIdx === 2) {
    function toggleStyle(style) {
      setSelectedStyles(prev =>
        prev.includes(style) ? prev.filter(s => s !== style) : [...prev, style]
      );
    }
    return (
      <div style={s.wrapper}>
        <div style={s.container}>
          <ProgressBar progress={progress} />
          <div style={s.screen}>
            <div style={s.headline}>Which styles do you actually drink?</div>
            <div style={s.subtext}>Select all that apply.</div>
            {STYLE_OPTIONS.map(style => (
              <div
                key={style}
                style={{
                  ...s.card,
                  ...(selectedStyles.includes(style) ? s.cardSelected : {})
                }}
                onClick={() => toggleStyle(style)}
              >
                <div style={s.cardName}>{style}</div>
              </div>
            ))}
            <div style={{ height: "24px" }} />
            <button
              style={{
                ...s.primaryButton,
                ...(selectedStyles.length ? {} : s.primaryButtonDisabled)
              }}
              onClick={handleStylesContinue}
              disabled={!selectedStyles.length}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== SCREENS: PAIRWISE QUESTIONS =====
  if (stepIdx >= STEPS_BEFORE_PAIRWISE && stepIdx < STEP_LOVES) {
    const qIdx = stepIdx - STEPS_BEFORE_PAIRWISE;
    const q = pairwiseQueue[qIdx];

    if (!q) {
      // Safety fallback — shouldn't happen
      return null;
    }

    const styleLabel = q.style === "white" ? "Whites" : "Reds";
    const questionsForStyle = pairwiseQueue.filter(pq => pq.style === q.style);
    const qNumInStyle = questionsForStyle.findIndex(pq => pq.id === q.id) + 1;
    const totalForStyle = questionsForStyle.length;

    return (
      <div style={s.wrapper}>
        <div style={s.container}>
          <ProgressBar progress={progress} />
          <div style={s.screen}>
            <div
              style={{
                fontSize: "12px",
                color: "#555",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                marginBottom: "24px"
              }}
            >
              {styleLabel} · {qNumInStyle} of {totalForStyle}
            </div>
            <div style={s.headline}>Which would you reach for?</div>
            <div style={{ height: "28px" }} />
            <WineCard wine={q.a} onSelect={() => handlePairwiseAnswer(q.id, "a")} />
            <div style={s.orDivider}>or</div>
            <WineCard wine={q.b} onSelect={() => handlePairwiseAnswer(q.id, "b")} />
            <div
              style={s.skipLink}
              onClick={() => handlePairwiseAnswer(q.id, "skip")}
            >
              No strong preference
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== SCREEN: LOVES & AVOIDS =====
  if (stepIdx === STEP_LOVES) {
    return (
      <div style={s.wrapper}>
        <div style={s.container}>
          <ProgressBar progress={progress} />
          <div style={s.screen}>
            <div style={s.headline}>
              Anything you always order — or always avoid?
            </div>
            <div style={s.subtext}>
              Optional. Specific wines, grapes, or styles. The more specific, the better.
            </div>

            <div style={s.sectionLabel}>Love</div>
            {loves.map((love, i) => (
              <input
                key={i}
                style={s.textInput}
                placeholder={
                  i === 0
                    ? "e.g. Puligny-Montrachet, big Napa Cab..."
                    : "Another?"
                }
                value={love}
                onChange={e => {
                  const next = [...loves];
                  next[i] = e.target.value;
                  setLoves(next);
                }}
              />
            ))}
            {loves.length < 8 && (
              <div
                style={s.addMore}
                onClick={() => setLoves([...loves, ""])}
              >
                + Add another
              </div>
            )}

            <div style={{ ...s.sectionLabel, marginTop: "12px" }}>Avoid</div>
            {avoids.map((avoid, i) => (
              <input
                key={i}
                style={s.textInput}
                placeholder={
                  i === 0
                    ? "e.g. oaky Chardonnay, sweet reds..."
                    : "Another?"
                }
                value={avoid}
                onChange={e => {
                  const next = [...avoids];
                  next[i] = e.target.value;
                  setAvoids(next);
                }}
              />
            ))}
            {avoids.length < 8 && (
              <div
                style={s.addMore}
                onClick={() => setAvoids([...avoids, ""])}
              >
                + Add another
              </div>
            )}

            <button style={s.primaryButton} onClick={handleFinish}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== SCREEN: COMPLETION =====
  if (stepIdx === STEP_COMPLETE) {
    return (
      <div style={s.wrapper}>
        <div style={s.container}>
          <ProgressBar progress={1} />
          <div
            style={{
              ...s.screen,
              justifyContent: "center",
              alignItems: "flex-start"
            }}
          >
            <div
              style={{ fontSize: "44px", marginBottom: "28px", lineHeight: 1 }}
            >
              🍷
            </div>
            <div style={s.headline}>Good. Now we're getting somewhere.</div>
            <div style={{ ...s.subtext, marginBottom: "48px" }}>
              Paste a wine list, scan one with your camera, or just type in a few wines — Davino will take it from there.
            </div>
            <button
              style={s.primaryButton}
              onClick={() => completedProfile && onComplete(completedProfile)}
            >
              Let's look at some wine
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
